/*
 * Entry point of the application.
 * Only platform bootstrapping code should be here.
 * For app-specific initialization, use `app/app.component.ts`.
 */

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, importProvidersFrom } from '@angular/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MatMomentDateModule,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { MatCardModule } from '@angular/material/card';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ApiPrefixInterceptor } from '@app/core/http/api-prefix.interceptor';
import { ErrorHandlerInterceptor } from '@app/core/http/error-handler.interceptor';
import { LoadingInterceptor } from '@app/core/http/loading.interceptor';
import { environment } from '@env/environment';
import {
  APP_ROUTE,
  APP_TITLE,
  MB_BASE_URL,
  MB_UMC_APPLICATION_KEY,
  MbBreadcrumbsService,
  MbLoadingBarModule,
  MbLoadingBarService,
  MbTesterPermissionsService,
} from '@montblancsimpl/core';
import { TranslateModule } from '@ngx-translate/core';
import { OktaAuth, OktaAuthOptions } from '@okta/okta-auth-js';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { AppRoutingModule } from '@app/app-routing.module';
import { AppComponent } from '@app/app.component';
import { MY_FORMATS } from '@app/app.module';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { CoreModule } from '@app/core/core.module';
import { AppRoute } from '@app/shared/app.route.enum';
import { HttpErrorInterceptor } from '@app/shared/services/http-error.interceptor';
import { MbCarouselService } from '@app/shared/components/carousel/carousel.service';
import { NewsCarouselService } from '@app/shared/components/news-carousel/news-carousel.service';
import { AuthenticationInterceptor } from '@app/core/authentication/authentication.interceptor';
import { provideOktaAuth, withOktaConfig } from '@okta/okta-angular';

if (environment.production) {
  enableProdMode();
}

const authConfig: OktaAuthOptions = {
  issuer: environment.oktaSettings.issuer,
  clientId: environment.oktaSettings.clientId,
  redirectUri: `${window.location.origin}/login/callback`,
  scopes: environment.oktaSettings.scopes,
  storageManager: {
    token: {
      storageType: 'sessionStorage',
    },
    transaction: {
      storageType: 'sessionStorage',
    },
  },
  tokenManager: {
    autoRenew: true,
  },
  services: {
    autoRenew: true,
    autoRemove: true,
    renewOnTabActivation: true,
  },
};

const oktaAuth = new OktaAuth(authConfig);

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      CoreModule,
      MatCardModule,
      MbLoadingBarModule,
      MatPaginatorModule,
      MatMenuModule,
      MatMomentDateModule,
      TranslateModule.forRoot(),
      ToastrModule.forRoot({
        timeOut: 6000,
        preventDuplicates: true,
      }),
      // MbTesterPermissionsModule.forRoot(
      //   Object.assign(new MbTesterPermissionsGlobalConfig(), { collapsed: true, isVisible: false } as MbTesterPermissionsGlobalConfig)
      // ),
      AppRoutingModule,
    ),
    MbCarouselService,
    NewsCarouselService,
    MbLoadingBarService,
    AuthenticationService,
    MbBreadcrumbsService,
    MbTesterPermissionsService,
    { provide: MB_BASE_URL, useValue: environment.apiUrl },
    { provide: APP_ROUTE, useValue: AppRoute },
    { provide: APP_TITLE, useValue: 'DIS' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    {
      provide: MB_UMC_APPLICATION_KEY,
      useValue: environment.umcApplicationKey,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiPrefixInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthenticationInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlerInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },

    ToastrService,
    provideAnimations(),
    provideOktaAuth(withOktaConfig({ oktaAuth })),
    provideHttpClient(withInterceptorsFromDi()),
  ],
}).catch((err) => console.log(err));
